import React, { useState } from "react";
import ConfirmationModal from "components/shared/confirmation-modal";
import Input from "components/input";
import ExpandableList from "components/shared/expandable-list";
import { useAppTranslation } from "locale/useAppTranslation";

interface DeactivateDriverModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => Promise<void>;
    driverName?: string;
    selectedDriverNames?: string[];
    isLoading?: boolean;
    isBulk?: boolean;
}

const DeactivateDriverModal: React.FC<DeactivateDriverModalProps> = ({
    open,
    onClose,
    onConfirm,
    driverName = "-",
    selectedDriverNames = [],
    isLoading = false,
    isBulk = false,
}) => {
    const { t } = useAppTranslation("driver");
    const [reason, setReason] = useState("");

    const handleConfirm = async () => {
        if (!reason.trim()) {
            return;
        }
        await onConfirm(reason);
    };

    React.useEffect(() => {
        if (!open) {
            setReason("");
        }
    }, [open]);

    return (
        <ConfirmationModal
            open={open}
            title={
                isBulk
                    ? t("bulk-deactivate-title") || "Deactivate Drivers"
                    : t("modal-deactivate-title") || "Deactivate Driver"
            }
            message={
                isBulk
                    ? t("bulk-deactivate-message", { count: selectedDriverNames.length }) || `Are you sure you want to deactivate ${selectedDriverNames.length} selected driver(s)?`
                    : t("modal-deactivate-message", { name: driverName }) || `Are you sure you want to deactivate ${driverName}?`
            }
            confirmLabel={t("btn-deactivate") || "Deactivate"}
            cancelLabel={t("btn-cancel") || "Cancel"}
            onConfirm={handleConfirm}
            onCancel={onClose}
            icon="warning"
            loading={isLoading}
            processingLabel={t("btn-processing") || "Processing..."}
        >
            <div className="mt-4 flex w-full flex-col gap-4 text-left">
                {isBulk && <ExpandableList items={selectedDriverNames} />}
                <Input
                    label={t("label-reason") || "Reason"}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("placeholder-deactivate-reason") || "Enter reason for deactivation"}
                    disabled={isLoading}
                    required
                />
            </div>
        </ConfirmationModal>
    );
};

export default DeactivateDriverModal;
